import React, { useMemo } from 'react';

import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  APPLICATION_PERIOD_TERM_INDEX,
  parseApplicationPeriodFromTerm,
  syncApplicationPeriodTerm,
} from '~/utils/societePayloadBuilder/statusCampaign/applicationPeriod';
import {
  CLAIM_CAMPAIGNS_URL,
  CONDITION_ICON_OPTIONS,
  SOCIETE_MATCH_TARGET_TIERS,
  TARGET_TIER_TOKEN,
} from '~/utils/societePayloadBuilder/statusCampaign/constants';
import { IMAGE_UPLOADER_URL } from '~/utils/societePayloadBuilder/constants';
import { createRowId } from '~/utils/societePayloadBuilder/ids';
import { createEmptyProgram } from '~/utils/societePayloadBuilder/statusCampaign/defaults';
import { PAYLOAD_BUILDER_PLACEHOLDERS } from '~/utils/societePayloadBuilder/placeholders';
import {
  defaultLogoImageId,
  findProgramByName,
  getLoyaltyProgramCategoryLabel,
  getLoyaltyProgramNamesByCategory,
} from '~/utils/societePayloadBuilder/statusCampaign/programs';
import { normalizeCampaignSlug } from '~/utils/societePayloadBuilder/statusCampaign/slug';
import type { StatusCampaignPayloadBuilderState, ProgramConfig } from '~/utils/societePayloadBuilder/statusCampaign/types';

import PayloadBuilderRegionChipsField from '../RegionChipsField';
import {
  FieldLabel,
  FieldLabelWithLink,
} from '../FieldLabels';
import { SectionCard } from '../SectionCard';
import { type PayloadFormSectionProps, updatePayloadState } from './StatusCampaignState';

export default function StatusMatchForm({ state, onChange }: PayloadFormSectionProps) {
  const programOptions = useMemo(() => getLoyaltyProgramNamesByCategory(), []);

  const handleSlugBlur = () => {
    const normalized = normalizeCampaignSlug(state.slug);
    if (normalized !== state.slug) {
      updatePayloadState(state, onChange, { slug: normalized });
    }
  };

  const handleApplicationPeriodChange = (field: 'applicationPeriodFrom' | 'applicationPeriodTo', value: string) => {
    const from = field === 'applicationPeriodFrom' ? value : state.applicationPeriodFrom;
    const to = field === 'applicationPeriodTo' ? value : state.applicationPeriodTo;
    updatePayloadState(state, onChange, {
      [field]: value,
      terms: syncApplicationPeriodTerm(state.terms, from, to),
    });
  };

  const handleApplicationPeriodTermBlur = (term: string) => {
    const parsed = parseApplicationPeriodFromTerm(term);
    if (!parsed) return;
    if (parsed.from === state.applicationPeriodFrom && parsed.to === state.applicationPeriodTo) {
      return;
    }
    updatePayloadState(state, onChange, {
      applicationPeriodFrom: parsed.from,
      applicationPeriodTo: parsed.to,
    });
  };

  const updateProgram = (programId: string, patch: Partial<ProgramConfig>) => {
    updatePayloadState(state, onChange, {
      programs: state.programs.map((program) => (program.id === programId ? { ...program, ...patch } : program)),
    });
  };

  const addProgram = () => {
    updatePayloadState(state, onChange, { programs: [...state.programs, createEmptyProgram()] });
  };

  const removeProgram = (programId: string) => {
    updatePayloadState(state, onChange, {
      programs: state.programs.filter((program) => program.id !== programId),
    });
  };

  return (
    <>
      <SectionCard title="Hero" subtitle="Headline, description, CTA, and hero image on the match page.">
        <TextField
          label="Title"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.heroTitleMatch}
          value={state.heroTitle}
          onChange={(event) => updatePayloadState(state, onChange, { heroTitle: event.target.value })}
          fullWidth
        />
        <TextField
          label="Subtitle"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.heroSubtitleMatch}
          value={state.heroSubtitle}
          onChange={(event) => updatePayloadState(state, onChange, { heroSubtitle: event.target.value })}
          fullWidth
        />
        <TextField
          label="Description"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.heroDescription}
          multiline
          minRows={3}
          value={state.heroDescription}
          onChange={(event) => updatePayloadState(state, onChange, { heroDescription: event.target.value })}
          fullWidth
        />
        <TextField
          label="CTA Text"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.heroCtaText}
          value={state.heroCtaText}
          onChange={(event) => updatePayloadState(state, onChange, { heroCtaText: event.target.value })}
          fullWidth
        />
        <Box>
          <FieldLabelWithLink label="Image ID" required href={IMAGE_UPLOADER_URL} />
          <TextField
            placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.heroImageId}
            value={state.heroImageId}
            onChange={(event) => updatePayloadState(state, onChange, { heroImageId: event.target.value })}
            fullWidth
            size="small"
          />
        </Box>
      </SectionCard>
      <SectionCard title="Campaign" subtitle="Campaign ID, URL slug, regions, and terms reference.">
        <Box>
          <FieldLabelWithLink label="Campaign ID" required href={CLAIM_CAMPAIGNS_URL} />
          <TextField
            placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.campaignId}
            value={state.campaignId}
            onChange={(event) => updatePayloadState(state, onChange, { campaignId: event.target.value })}
            fullWidth
            size="small"
          />
        </Box>
        <TextField
          label="URL Slug"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.urlSlug}
          helperText="Lowercase letters, numbers, and hyphens only. Paste a full /societe/… URL to extract the slug."
          value={state.slug}
          onChange={(event) => updatePayloadState(state, onChange, { slug: event.target.value })}
          onBlur={handleSlugBlur}
          fullWidth
        />
        <TextField
          label="Terms Slug"
          required
          placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.termsSlugMatch}
          value={state.termsSlug}
          onChange={(event) => updatePayloadState(state, onChange, { termsSlug: event.target.value })}
          fullWidth
        />
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Application period (AEDT)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Updates the applications open/close term automatically. You can still edit that term directly below.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Opens"
              required
              type="datetime-local"
              value={state.applicationPeriodFrom}
              onChange={(event) => handleApplicationPeriodChange('applicationPeriodFrom', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Closes"
              required
              type="datetime-local"
              value={state.applicationPeriodTo}
              onChange={(event) => handleApplicationPeriodChange('applicationPeriodTo', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </Box>
        <PayloadBuilderRegionChipsField
          label="Allowed Regions"
          helperText="Site region codes — click to add or remove"
          regions={state.allowedRegions ?? []}
          onChange={(allowedRegions) => updatePayloadState(state, onChange, { allowedRegions })}
          required
        />
      </SectionCard>
      <SectionCard
        title="Programs"
        count={state.programs.length}
        subtitle="Partner loyalty programs and Société tier mappings."
      >
        {state.programs.map((program, index) => {
          const tiers = findProgramByName(program.programName)?.tiers ?? [];
          return (
            <Paper key={program.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.1em' }}>
                  PROGRAM {index + 1}
                </Typography>
                <IconButton size="small" aria-label="Remove program" onClick={() => removeProgram(program.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Autocomplete
                options={programOptions}
                groupBy={getLoyaltyProgramCategoryLabel}
                value={program.programName || null}
                onChange={(_, value) => {
                  const programName = value ?? '';
                  updateProgram(program.id, {
                    programName,
                    logoImageId: programName ? defaultLogoImageId(programName) : '',
                    tierMappings: [],
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Program" required placeholder="Search loyalty programs…" />
                )}
              />
              <Box sx={{ mt: 2 }}>
                <FieldLabelWithLink label="Logo Image ID" required href={IMAGE_UPLOADER_URL} />
                <TextField
                  placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.programLogoImageId}
                  value={program.logoImageId}
                  onChange={(event) => updateProgram(program.id, { logoImageId: event.target.value })}
                  fullWidth
                  size="small"
                />
              </Box>
              <FieldLabel required sx={{ mt: 2, mb: 1 }}>
                Tier mapping
              </FieldLabel>
              <Stack spacing={1}>
                {program.tierMappings.map((mapping) => (
                  <Stack key={mapping.id} direction="row" spacing={1} alignItems="center">
                    <TextField
                      select
                      label="Source tier"
                      required
                      value={mapping.sourceTier}
                      onChange={(event) =>
                        updateProgram(program.id, {
                          tierMappings: program.tierMappings.map((row) =>
                            row.id === mapping.id ? { ...row, sourceTier: event.target.value } : row,
                          ),
                        })
                      }
                      sx={{ flex: 1 }}
                      size="small"
                      disabled={!program.programName}
                    >
                      {tiers.map((tier) => (
                        <MenuItem key={tier} value={tier}>
                          {tier}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Typography color="text.secondary">→</Typography>
                    <TextField
                      select
                      label="Société tier"
                      required
                      value={mapping.targetSocieteTier}
                      onChange={(event) =>
                        updateProgram(program.id, {
                          tierMappings: program.tierMappings.map((row) =>
                            row.id === mapping.id ? { ...row, targetSocieteTier: event.target.value } : row,
                          ),
                        })
                      }
                      sx={{ flex: 1 }}
                      size="small"
                    >
                      {SOCIETE_MATCH_TARGET_TIERS.map((tier) => (
                        <MenuItem key={tier} value={tier}>
                          {tier}
                        </MenuItem>
                      ))}
                    </TextField>
                    <IconButton
                      size="small"
                      onClick={() =>
                        updateProgram(program.id, {
                          tierMappings: program.tierMappings.filter((row) => row.id !== mapping.id),
                        })
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Button
                size="small"
                startIcon={<AddIcon />}
                sx={{ mt: 1 }}
                disabled={!program.programName}
                onClick={() =>
                  updateProgram(program.id, {
                    tierMappings: [
                      ...program.tierMappings,
                      { id: createRowId(), sourceTier: '', targetSocieteTier: '' },
                    ],
                  })
                }
              >
                Add tier row
              </Button>
            </Paper>
          );
        })}
        <Button variant="outlined" fullWidth startIcon={<AddIcon />} onClick={addProgram}>
          Add program
        </Button>
      </SectionCard>
      <SectionCard
        title="Conditions"
        count={state.conditions.length}
        subtitle="Eligibility bullets shown on the campaign page (max 4)."
      >
        {state.conditions.map((condition, index) => (
          <Paper key={condition.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                CONDITION {index + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  updatePayloadState(state, onChange, {
                    conditions: state.conditions.filter((row) => row.id !== condition.id),
                  })
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField
              select
              label="Icon"
              value={condition.icon}
              onChange={(event) =>
                updatePayloadState(state, onChange, {
                  conditions: state.conditions.map((row) =>
                    row.id === condition.id ? { ...row, icon: event.target.value } : row,
                  ),
                })
              }
              fullWidth
              size="small"
            >
              {CONDITION_ICON_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Title"
              required
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.conditionTitle}
              value={condition.title}
              onChange={(event) =>
                updatePayloadState(state, onChange, {
                  conditions: state.conditions.map((row) =>
                    row.id === condition.id ? { ...row, title: event.target.value } : row,
                  ),
                })
              }
              fullWidth
              sx={{ mt: 1 }}
            />
            <TextField
              label="Description"
              required
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.conditionDescription}
              multiline
              minRows={2}
              value={condition.description}
              onChange={(event) =>
                updatePayloadState(state, onChange, {
                  conditions: state.conditions.map((row) =>
                    row.id === condition.id ? { ...row, description: event.target.value } : row,
                  ),
                })
              }
              fullWidth
              sx={{ mt: 1 }}
            />
          </Paper>
        ))}
        <Button
          variant="outlined"
          disabled={state.conditions.length >= 4}
          onClick={() =>
            updatePayloadState(state, onChange, {
              conditions: [...state.conditions, { id: createRowId(), icon: 'clock', title: '', description: '' }],
            })
          }
        >
          Add condition
        </Button>
      </SectionCard>
      <SectionCard
        title="How it works"
        count={state.howItWorks.length}
        subtitle="Step-by-step explanation of the status match flow."
      >
        {state.howItWorks.map((step, index) => (
          <Paper key={step.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                STEP {index + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  updatePayloadState(state, onChange, {
                    howItWorks: state.howItWorks.filter((row) => row.id !== step.id),
                  })
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField
              label="Title"
              required
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.stepTitle}
              value={step.title}
              onChange={(event) =>
                updatePayloadState(state, onChange, {
                  howItWorks: state.howItWorks.map((row) =>
                    row.id === step.id ? { ...row, title: event.target.value } : row,
                  ),
                })
              }
              fullWidth
            />
            <TextField
              label="Description"
              required
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.stepDescription}
              multiline
              minRows={2}
              value={step.description}
              onChange={(event) =>
                updatePayloadState(state, onChange, {
                  howItWorks: state.howItWorks.map((row) =>
                    row.id === step.id ? { ...row, description: event.target.value } : row,
                  ),
                })
              }
              fullWidth
              sx={{ mt: 1 }}
            />
          </Paper>
        ))}
        <Button
          variant="outlined"
          onClick={() =>
            updatePayloadState(state, onChange, {
              howItWorks: [...state.howItWorks, { id: createRowId(), title: '', description: '' }],
            })
          }
        >
          Add step
        </Button>
      </SectionCard>
      <SectionCard title="Challenge card" subtitle="In-app card copy shown after a member applies.">
        <Box>
          <FieldLabel required>Card title</FieldLabel>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={TARGET_TIER_TOKEN} size="small" sx={{ fontFamily: 'monospace' }} />
            <TextField
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.challengeCardTitleSuffix}
              value={state.challengeCardTitleSuffix}
              onChange={(event) =>
                updatePayloadState(state, onChange, { challengeCardTitleSuffix: event.target.value })
              }
              fullWidth
              size="small"
            />
          </Stack>
        </Box>
        <Box>
          <FieldLabel required>Card subtitle</FieldLabel>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.challengeCardSubtitleBefore}
              value={state.challengeCardSubtitleBefore}
              onChange={(event) =>
                updatePayloadState(state, onChange, { challengeCardSubtitleBefore: event.target.value })
              }
              sx={{ flex: 1, minWidth: 200 }}
              size="small"
            />
            <Chip label={TARGET_TIER_TOKEN} size="small" sx={{ fontFamily: 'monospace' }} />
            <TextField
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.challengeCardSubtitleAfter}
              value={state.challengeCardSubtitleAfter}
              onChange={(event) =>
                updatePayloadState(state, onChange, { challengeCardSubtitleAfter: event.target.value })
              }
              sx={{ flex: 1, minWidth: 120 }}
              size="small"
            />
          </Stack>
        </Box>
        <TextField
          select
          label="Background type"
          value={state.challengeCardBackground.type}
          onChange={(event) => {
            const type = event.target.value as StatusCampaignPayloadBuilderState['challengeCardBackground']['type'];
            updatePayloadState(state, onChange, {
              challengeCardBackground:
                type === 'tierGradient'
                  ? { type: 'tierGradient' }
                  : type === 'image'
                  ? { type: 'image', imageId: '' }
                  : { type: 'color', value: '#ffffff' },
            });
          }}
          fullWidth
        >
          <MenuItem value="color">Color</MenuItem>
          <MenuItem value="tierGradient">Tier gradient</MenuItem>
          <MenuItem value="image">Image</MenuItem>
        </TextField>
        {state.challengeCardBackground.type === 'color' ? (
          <TextField
            label="Background color"
            required
            placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.backgroundColor}
            value={state.challengeCardBackground.value ?? ''}
            onChange={(event) =>
              updatePayloadState(state, onChange, {
                challengeCardBackground: { type: 'color', value: event.target.value },
              })
            }
            fullWidth
          />
        ) : null}
        {state.challengeCardBackground.type === 'image' ? (
          <TextField
            label="Background image ID"
            required
            placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.backgroundImageId}
            value={state.challengeCardBackground.imageId ?? ''}
            onChange={(event) =>
              updatePayloadState(state, onChange, {
                challengeCardBackground: { type: 'image', imageId: event.target.value },
              })
            }
            fullWidth
          />
        ) : null}
      </SectionCard>
      <SectionCard title="Evidence" subtitle="Photo upload limits, hints, and proof requirements.">
        <TextField
          label="Minimum photos"
          required
          type="number"
          value={state.evidenceMinPhotos}
          onChange={(event) =>
            updatePayloadState(state, onChange, { evidenceMinPhotos: Number(event.target.value) || 0 })
          }
          fullWidth
        />
        <Box>
          <FieldLabelWithLink label="Verification image ID" required href={IMAGE_UPLOADER_URL} />
          <TextField
            placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.evidenceVerificationImageId}
            value={state.evidenceVerificationImageId}
            onChange={(event) =>
              updatePayloadState(state, onChange, { evidenceVerificationImageId: event.target.value })
            }
            fullWidth
            size="small"
          />
        </Box>
        {(['evidencePhotoHints', 'evidenceAcceptedFormats', 'evidenceProofRequirements'] as const).map((fieldKey) => (
          <Box key={fieldKey}>
            <FieldLabel required>
              {fieldKey === 'evidencePhotoHints'
                ? 'Photo hints'
                : fieldKey === 'evidenceAcceptedFormats'
                ? 'Accepted formats'
                : 'Proof requirements'}
            </FieldLabel>
            {state[fieldKey].map((value, index) => (
              <Stack key={`${fieldKey}-${index}`} direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  placeholder={
                    fieldKey === 'evidencePhotoHints'
                      ? PAYLOAD_BUILDER_PLACEHOLDERS.evidenceHint
                      : fieldKey === 'evidenceAcceptedFormats'
                      ? PAYLOAD_BUILDER_PLACEHOLDERS.evidenceFormat
                      : PAYLOAD_BUILDER_PLACEHOLDERS.evidenceRequirement
                  }
                  value={value}
                  onChange={(event) => {
                    const next = [...state[fieldKey]];
                    next[index] = event.target.value;
                    updatePayloadState(state, onChange, { [fieldKey]: next });
                  }}
                  fullWidth
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const next = state[fieldKey].filter((_, rowIndex) => rowIndex !== index);
                    updatePayloadState(state, onChange, { [fieldKey]: next });
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              onClick={() => updatePayloadState(state, onChange, { [fieldKey]: [...state[fieldKey], ''] })}
            >
              Add row
            </Button>
          </Box>
        ))}
      </SectionCard>
      <SectionCard
        title="Success steps"
        count={state.successSteps.length}
        subtitle="Milestones shown after submission (e.g. qualifying stay, review)."
      >
        {state.successSteps.map((step) => (
          <Paper key={step.key} variant="outlined" sx={{ p: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1, display: 'block' }}
            >
              {step.key}
            </Typography>
            <TextField
              label="Title"
              required
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.successStepTitle}
              value={step.title}
              onChange={(event) =>
                updatePayloadState(state, onChange, {
                  successSteps: state.successSteps.map((row) =>
                    row.key === step.key ? { ...row, title: event.target.value } : row,
                  ),
                })
              }
              fullWidth
            />
            <TextField
              label="Description"
              required
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.successStepDescription}
              multiline
              minRows={2}
              value={step.description}
              onChange={(event) =>
                updatePayloadState(state, onChange, {
                  successSteps: state.successSteps.map((row) =>
                    row.key === step.key ? { ...row, description: event.target.value } : row,
                  ),
                })
              }
              fullWidth
              sx={{ mt: 1 }}
            />
          </Paper>
        ))}
      </SectionCard>
      <SectionCard
        title="Terms"
        count={state.terms.length}
        subtitle="Terms and conditions copy shown on the campaign page."
      >
        {state.terms.map((term, index) => (
          <Stack key={`term-${index}`} direction="row" spacing={1}>
            <TextField
              label={
                index === APPLICATION_PERIOD_TERM_INDEX ? `Term ${index + 1} (application period)` : `Term ${index + 1}`
              }
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.term}
              multiline
              minRows={2}
              value={term}
              onChange={(event) => {
                const next = [...state.terms];
                next[index] = event.target.value;
                updatePayloadState(state, onChange, { terms: next });
              }}
              onBlur={index === APPLICATION_PERIOD_TERM_INDEX ? () => handleApplicationPeriodTermBlur(term) : undefined}
              fullWidth
            />
            <IconButton
              onClick={() =>
                updatePayloadState(state, onChange, {
                  terms: state.terms.filter((_, rowIndex) => rowIndex !== index),
                })
              }
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
        <Button variant="outlined" onClick={() => updatePayloadState(state, onChange, { terms: [...state.terms, ''] })}>
          Add term
        </Button>
      </SectionCard>
      <SectionCard title="FAQs" count={state.faqs.length} subtitle="Question and answer pairs on the campaign page.">
        {state.faqs.map((faq, index) => (
          <Paper key={faq.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                FAQ {index + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  updatePayloadState(state, onChange, {
                    faqs: state.faqs.filter((row) => row.id !== faq.id),
                  })
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField
              label="Question"
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.faqQuestion}
              value={faq.question}
              onChange={(event) =>
                updatePayloadState(state, onChange, {
                  faqs: state.faqs.map((row) => (row.id === faq.id ? { ...row, question: event.target.value } : row)),
                })
              }
              fullWidth
            />
            <TextField
              label="Answer"
              placeholder={PAYLOAD_BUILDER_PLACEHOLDERS.faqAnswer}
              multiline
              minRows={2}
              value={faq.answer}
              onChange={(event) =>
                updatePayloadState(state, onChange, {
                  faqs: state.faqs.map((row) => (row.id === faq.id ? { ...row, answer: event.target.value } : row)),
                })
              }
              fullWidth
              sx={{ mt: 1 }}
            />
          </Paper>
        ))}
        <Button
          variant="outlined"
          onClick={() =>
            updatePayloadState(state, onChange, {
              faqs: [...state.faqs, { id: createRowId(), question: '', answer: '' }],
            })
          }
        >
          Add FAQ
        </Button>
      </SectionCard>
    </>
  );
}
